/**
 * Event roles and who holds them — D62.
 *
 * Everyone at an event who is not a competitor: the MC and co-host, the arena director, head
 * dancers, the host drum, invited drums, flag and staff carriers, elders, the stickman, volunteers
 * and staff. A role is a row rather than an enum member, so the community with a firekeeper or a
 * water carrier adds one without a code change.
 */

import { z } from "zod";
import {
  centsSchema,
  longTextSchema,
  optionalText,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

export const ROLE_SUBJECT_KINDS = ["person", "group", "either"] as const;
export const ROLE_ASSIGNMENT_STATUSES = ["expected", "confirmed", "withdrawn"] as const;

/**
 * A role definition.
 *
 * `minCount`/`maxCount` are what make "arena director — only one" and "head dancers — multiple" the
 * same mechanism. Both are optional in the sense that 0 and null are meaningful and common: 0 with
 * an unbounded maximum is the elder who might simply show up, and refusing to record them would be
 * refusing to record the thing the experts actually described.
 */
export const createEventRoleSchema = z
  .object({
    name: shortTextSchema,
    /** `optionalText`, not `.optional()`: the roster dialog submits one body whether it is
     *  creating or editing, and a box left blank arrives as null either way. */
    description: optionalText(longTextSchema),
    /** How the roster groups it on screen — "Arena", "Drums", "Crew". */
    groupLabel: optionalText(shortTextSchema),
    subjectKind: z.enum(ROLE_SUBJECT_KINDS).default("person"),
    minCount: z.number().int().min(0).max(500).default(0),
    /** Null is unbounded, which is head dancers, elders and flag carriers. */
    maxCount: z.number().int().min(1).max(500).nullable().optional(),
    expectsCheckin: z.boolean().default(true),
    publiclyListed: z.boolean().default(true),
  })
  .refine((role) => role.maxCount == null || role.maxCount >= role.minCount, {
    message: "The most cannot be fewer than the fewest",
    path: ["maxCount"],
  });

export const updateEventRoleSchema = z
  .object({
    name: shortTextSchema.optional(),
    description: longTextSchema.nullable().optional(),
    groupLabel: shortTextSchema.nullable().optional(),
    subjectKind: z.enum(ROLE_SUBJECT_KINDS).optional(),
    minCount: z.number().int().min(0).max(500).optional(),
    maxCount: z.number().int().min(1).max(500).nullable().optional(),
    expectsCheckin: z.boolean().optional(),
    publiclyListed: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine(
    (role) => role.maxCount == null || role.minCount == null || role.maxCount >= role.minCount,
    { message: "The most cannot be fewer than the fewest", path: ["maxCount"] },
  );

/**
 * Someone holding a role.
 *
 * `displayName` is the only thing required, deliberately. Listing the MC on the poster must not
 * wait on a participant record, and the person who "might just show up" is exactly the one that
 * requirement would fail. A payee is what makes an assignment payable, and the API says so at the
 * point money is calculated rather than at the point a name is typed (D62).
 *
 * An empty `sessionIds` means every session, the same convention registration windows and
 * competitions already use (D49, D50).
 */
export const createRoleAssignmentSchema = z
  .object({
    eventRoleId: ulidSchema,
    displayName: shortTextSchema,
    groupName: optionalText(shortTextSchema),
    participantId: ulidSchema.nullable().optional(),
    teamId: ulidSchema.nullable().optional(),
    userId: ulidSchema.nullable().optional(),
    registrationId: ulidSchema.nullable().optional(),
    /** For a rule whose basis is `agreed` — the fee settled with this person (D63). */
    agreedAmountCents: centsSchema.nullable().optional(),
    sessionIds: z.array(ulidSchema).optional(),
    status: z.enum(ROLE_ASSIGNMENT_STATUSES).default("confirmed"),
    notes: optionalText(longTextSchema),
  })
  .refine((assignment) => assignment.participantId == null || assignment.teamId == null, {
    message: "A role holder is a person or a group, not both",
    path: ["teamId"],
  });

export const updateRoleAssignmentSchema = z
  .object({
    displayName: shortTextSchema.optional(),
    groupName: shortTextSchema.nullable().optional(),
    participantId: ulidSchema.nullable().optional(),
    teamId: ulidSchema.nullable().optional(),
    userId: ulidSchema.nullable().optional(),
    registrationId: ulidSchema.nullable().optional(),
    agreedAmountCents: centsSchema.nullable().optional(),
    /** A full restatement, not a merge — an empty array means every session, which an absent
     *  field could not be told from "leave it alone". */
    sessionIds: z.array(ulidSchema).optional(),
    status: z.enum(ROLE_ASSIGNMENT_STATUSES).optional(),
    notes: longTextSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((assignment) => assignment.participantId == null || assignment.teamId == null, {
    message: "A role holder is a person or a group, not both",
    path: ["teamId"],
  });

/** Copying a community's saved role list into an event, the way a dimension template is (D43). */
export const applyRoleTemplatesSchema = z.object({
  /** Empty or absent means every template the community has. */
  roleIds: z.array(ulidSchema).optional(),
});

export type EventRoleSubjectKind = (typeof ROLE_SUBJECT_KINDS)[number];
export type RoleAssignmentStatus = (typeof ROLE_ASSIGNMENT_STATUSES)[number];
export type CreateEventRoleInput = z.infer<typeof createEventRoleSchema>;
export type CreateRoleAssignmentInput = z.infer<typeof createRoleAssignmentSchema>;
